import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    getInsiderProgramService,
    getLearningProgramPreviewService,
    getLearningProgramSlidePreviewService,
    getUnrestrictedLearningProgramPreviewService,
    getUnrestrictedLearningProgramSlidePreviewService,
    getLearningProgramStatusService,
    getTemplateProgramPreviewService,
    getTemplateProgramSlidePreviewService,
    listV3Level7DQCodeService,
    updateAssessmentVisibilityLearningProgramService,
} from '../../../../../services/learning_programs.service';
import { toastWarning, getTTSLanguageCode } from '../../../../../utils/utils';
import { normalizeUiLocale, tUi } from '../../../../../utils/dq_lc';
import {
    getProgramPreviewAndAssessmentsFromResponse,
    mapProgramToSidebar,
    getSegmentDisplayName,
} from '../../utils/LearnTab/preview.utils';

function useInterval(callback, delay) {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

export const useProgramData = ({
    code,
    locationState = null,
    embedded = false,
    readOnly = false,
    isPublicPreview = false,
    setSegments,
    setActiveSegment,
    setCurrentSegment,
    setCurrentSlide,
    landOnLastSlideRef,
    showEnglishContent = false,
}) => {
    // Template vs Normal services
    const fetchProgramPreview = readOnly
        ? getTemplateProgramPreviewService
        : getLearningProgramPreviewService;
    const fetchSlidePreview = readOnly
        ? getTemplateProgramSlidePreviewService
        : getLearningProgramSlidePreviewService;

    const [programData, setProgramData] = useState(locationState?.assessment || null);
    const [modulesData, setModulesData] = useState([]);
    const [previewResponse, setPreviewResponse] = useState(null);

    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);

    const [openModules, setOpenModules] = useState({});
    const [openUnits, setOpenUnits] = useState({});
    const [openSessions, setOpenSessions] = useState({});

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [slidesLoading, setSlidesLoading] = useState(false);

    const [contentStatus, setContentStatus] = useState({});
    const [allSessionsDone, setAllSessionsDone] = useState(false);
    const [statusPolling, setStatusPolling] = useState(false);
    const lastStatusFetchTimeRef = useRef(null);

    // Multi-language states
    const [availablePreviewLanguages, setAvailablePreviewLanguages] = useState([]);
    const [selectedPreviewLanguage, setSelectedPreviewLanguage] = useState('en-US');
    const [currentDisplayLanguage, setCurrentDisplayLanguage] = useState('en-US');
    const [availableSlideLanguages, setAvailableSlideLanguages] = useState([]);
    const [cachedSlideData, setCachedSlideData] = useState({});

    // Session slides and assessments
    const [sessionSlides, setSessionSlides] = useState({});
    const [sessionAssessments, setSessionAssessments] = useState({});
    const [introVideoUrl, setIntroVideoUrl] = useState(null);
    const [introVideoVtt, setIntroVideoVtt] = useState(null);

    // Assessment visibility: { session_code: { show_pre_assessment: bool, show_post_assessment: bool } }
    const [assessmentVisibility, setAssessmentVisibility] = useState({});

    // Assessment visibility modal state & handlers
    const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
    const [assessmentModalType, setAssessmentModalType] = useState(null);
    const [assessmentModalAction, setAssessmentModalAction] = useState(null);

    const handleOpenAssessmentModal = useCallback((assessmentType, currentVisibility) => {
        setAssessmentModalType(assessmentType);
        setAssessmentModalAction(currentVisibility ? 'hide' : 'unhide');
        setAssessmentModalOpen(true);
    }, []);

    const handleCloseAssessmentModal = useCallback(() => {
        setAssessmentModalOpen(false);
        setAssessmentModalType(null);
        setAssessmentModalAction(null);
    }, []);

    const handleConfirmAssessmentVisibility = useCallback(async () => {
        if (!selectedSession || !assessmentModalType || !programData?.code) return;

        try {
            const sessionCode = selectedSession.session_code;
            const newValue = assessmentModalAction === 'unhide';
            const updatedVisibility = {
                show_pre_assessment: newValue,
                show_post_assessment: newValue
            };

            await updateAssessmentVisibilityLearningProgramService(
                programData.code,
                sessionCode,
                updatedVisibility.show_pre_assessment,
                updatedVisibility.show_post_assessment
            );

            setAssessmentVisibility(prev => ({
                ...prev,
                [sessionCode]: updatedVisibility
            }));

            handleCloseAssessmentModal();
        } catch (error) {
            console.error('Error updating assessment visibility:', error);
        }
    }, [selectedSession, assessmentModalType, programData?.code, assessmentModalAction, handleCloseAssessmentModal]);

    // DQ Code popover & meta list
    const [dqcoList, setDqcoList] = useState(locationState?.dqcoList || []);
    const [dqcoLoadingCode, setDqcoLoadingCode] = useState(null);
    const [popupContent, setPopupContent] = useState(null);

    const sidebarProgramName = useMemo(() => {
        const otherLangKey = Object.keys(programData?.translations || {}).find(lang => lang !== 'en-US');
        const otherTranslation = otherLangKey ? programData?.translations?.[otherLangKey] : null;
        return (otherTranslation?.full_name || otherTranslation?.name || '').trim() || programData?.full_name || '';
    }, [programData]);

    const translationKeys = Object.keys(programData?.translations || {});
    const nonEnglishTranslationKey = translationKeys.find(lang => lang && lang !== 'en-US');
    const effectiveContentLanguage = showEnglishContent ? 'en-US' : (currentDisplayLanguage !== 'en-US' ? currentDisplayLanguage : (nonEnglishTranslationKey || currentDisplayLanguage));
    const uiLocale = normalizeUiLocale(effectiveContentLanguage);

    const ttsLang = getTTSLanguageCode(currentDisplayLanguage ? currentDisplayLanguage.split('-')[0] : 'en');

    // Fetch Program Data
    useEffect(() => {
        const fetchProgram = async () => {
            try {
                if (!programData && code && !readOnly) {
                    setLoading(true);
                    const response = await getInsiderProgramService(code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgram();
    }, [programData, code, readOnly]);

    // Build segments list helper
    const buildSegmentsList = useCallback((slideData, session, sessionAssessmentsOverride = null) => {
        const base = Object.keys(slideData || {})
            .filter(segmentKey => Array.isArray(slideData[segmentKey]))
            .map(segmentKey => ({ id: segmentKey, name: getSegmentDisplayName(segmentKey, session, uiLocale), kind: 'slides' }));

        if (embedded) return base.filter(s => s.id !== 'Seg5_1');

        const sessKey = session?.session_code;
        const assessData = sessionAssessmentsOverride !== null ? sessionAssessmentsOverride : sessionAssessments;
        const assess = assessData?.[sessKey] || {};
        const hasPre = Array.isArray(assess.pre) && assess.pre.length > 0;
        const hasPost = Array.isArray(assess.post) && assess.post.length > 0;
        let list = [...base];

        if (hasPre) {
            const idxIntro = list.findIndex(s => s.id === 'Intro' || s.id === 'Seg1');
            const insertIdx = idxIntro >= 0 ? idxIntro + 1 : 0;
            list.splice(insertIdx, 0, { id: '__PRE_ASSESSMENT__', name: tUi('preview.preAssessment', uiLocale), kind: 'assessment', assessType: 'pre' });
        }

        if (hasPost) {
            const idxRecap = list.findIndex(s => s.id === 'Closing' || s.id === 'Seg5');
            if (idxRecap >= 0) {
                list.splice(idxRecap, 0, { id: '__POST_ASSESSMENT__', name: tUi('preview.postAssessment', uiLocale), kind: 'assessment', assessType: 'post' });
            } else {
                list.push({ id: '__POST_ASSESSMENT__', name: tUi('preview.postAssessment', uiLocale), kind: 'assessment', assessType: 'post' });
            }
        }
        return list;
    }, [embedded, sessionAssessments, uiLocale]);

    // Fetch first session content
    const fetchFirstSessionContent = useCallback(async (session, sessionAssessmentsOverride = null) => {
        if (!session) {
            setInitialLoading(false);
            return;
        }

        const sessionStatusData = contentStatus[session.session_code];
        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;
        if (sessionStatus && sessionStatus !== 'done') {
            setInitialLoading(false);
            return;
        }

        setSlidesLoading(true);
        try {
            const result = await (isPublicPreview ? getUnrestrictedLearningProgramSlidePreviewService(
                programData?.code || code,
                session.session_code
            ) : fetchSlidePreview(
                programData?.code || code,
                session.session_code
            ));

            let slideData = null;
            let showPreAssessment = true;
            let showPostAssessment = true;

            if (result?.data?.slide_previews_by_language) {
                const slidesByLang = result.data.slide_previews_by_language;
                const availableLangs = Object.keys(slidesByLang);
                setAvailableSlideLanguages(availableLangs);

                const cachedData = {};
                availableLangs.forEach(lang => {
                    cachedData[lang] = slidesByLang[lang];
                });
                setCachedSlideData(cachedData);
                let languageToUse = currentDisplayLanguage || selectedPreviewLanguage;

                if (!slidesByLang[languageToUse] && availableLangs.length > 0) {
                    if (availableLangs.length > 1 && !showEnglishContent) {
                        const nonEnglishLang = availableLangs.find(l => l !== 'en-US');
                        languageToUse = nonEnglishLang || availableLangs[0];
                    } else {
                        languageToUse = availableLangs[0];
                    }
                }
                let languageData = slidesByLang[languageToUse];

                if (!languageData && availableLangs.length > 0) {
                    languageToUse = availableLangs[0];
                    languageData = slidesByLang[languageToUse];
                }

                if (languageData) {
                    slideData = languageData.slide_preview;
                    showPreAssessment = languageData.show_pre_assessment ?? true;
                    showPostAssessment = languageData.show_post_assessment ?? true;
                    setIntroVideoUrl(languageData.IntroVideo || null);
                    setIntroVideoVtt(languageData.IntroVideoVtt || null);
                    setCurrentDisplayLanguage(languageToUse);
                }
            } else if (result?.data?.slide_preview) {
                slideData = result.data.slide_preview;
                showPreAssessment = result.data.show_pre_assessment ?? true;
                showPostAssessment = result.data.show_post_assessment ?? true;
                setIntroVideoUrl(result.data.IntroVideo || null);
                setIntroVideoVtt(result.data.IntroVideoVtt || null);
                setCurrentDisplayLanguage('en-US');
                setAvailableSlideLanguages(['en-US']);
            }

            if (slideData) {
                setAssessmentVisibility(prev => ({
                    ...prev,
                    [session.session_code]: {
                        show_pre_assessment: showPreAssessment,
                        show_post_assessment: showPostAssessment
                    }
                }));

                setSessionSlides(slideData);
                setOpenSessions(prev => ({ ...prev, [session.session_code]: true }));

                const segmentsList = buildSegmentsList(slideData, session, sessionAssessmentsOverride);
                if (setSegments) setSegments(segmentsList);

                if (segmentsList.length > 0) {
                    setSelectedSession(session);
                    setSelectedSection('Session Details');
                    if (setActiveSegment) setActiveSegment(segmentsList[0].id);
                    if (setCurrentSegment) setCurrentSegment(0);
                    if (setCurrentSlide) setCurrentSlide(0);
                }
            }
        } catch (err) {
            console.error("Error fetching first session slides:", err);
        } finally {
            setSlidesLoading(false);
            setInitialLoading(false);
        }
    }, [programData, contentStatus, isPublicPreview, code, fetchSlidePreview, selectedPreviewLanguage, showEnglishContent, buildSegmentsList, setSegments, setActiveSegment, setCurrentSegment, setCurrentSlide]);

    const fetchFirstSessionContentRef = useRef(fetchFirstSessionContent);
    useEffect(() => {
        fetchFirstSessionContentRef.current = fetchFirstSessionContent;
    }, [fetchFirstSessionContent]);

    // Fetch Session Slides
    const fetchSessionSlides = useCallback(async (session, startAtLastSegment = false) => {
        if (!session) return;

        setSlidesLoading(true);
        setSelectedSession(session);

        try {
            const result = await (isPublicPreview ? getUnrestrictedLearningProgramSlidePreviewService(
                programData?.code || code,
                session.session_code
            ) : fetchSlidePreview(
                programData?.code || code,
                session.session_code
            ));

            let slideData = null;
            let showPreAssessment = true;
            let showPostAssessment = true;

            if (result?.data?.slide_previews_by_language) {
                const slidesByLang = result.data.slide_previews_by_language;
                const availableLangs = Object.keys(slidesByLang);
                setAvailableSlideLanguages(availableLangs);

                const cachedData = {};
                availableLangs.forEach(lang => {
                    cachedData[lang] = slidesByLang[lang];
                });
                setCachedSlideData(cachedData);

                let languageToUse = currentDisplayLanguage || selectedPreviewLanguage;
                if (!slidesByLang[languageToUse] && availableLangs.length > 0) {
                    if (availableLangs.length > 1 && !showEnglishContent) {
                        const nonEnglishLang = availableLangs.find(l => l !== 'en-US');
                        languageToUse = nonEnglishLang || availableLangs[0];
                    } else {
                        languageToUse = availableLangs[0];
                    }
                }

                let languageData = slidesByLang[languageToUse];
                if (!languageData && availableLangs.length > 0) {
                    languageToUse = availableLangs[0];
                    languageData = slidesByLang[languageToUse];
                }

                if (languageData) {
                    slideData = languageData.slide_preview;
                    showPreAssessment = languageData.show_pre_assessment ?? true;
                    showPostAssessment = languageData.show_post_assessment ?? true;
                    setIntroVideoUrl(languageData.IntroVideo || null);
                    setIntroVideoVtt(languageData.IntroVideoVtt || null);
                    setCurrentDisplayLanguage(languageToUse);
                }
            } else if (result?.data?.slide_preview) {
                slideData = result.data.slide_preview;
                showPreAssessment = result.data.show_pre_assessment ?? true;
                showPostAssessment = result.data.show_post_assessment ?? true;
                setIntroVideoUrl(result.data.IntroVideo || null);
                setIntroVideoVtt(result.data.IntroVideoVtt || null);
                setCurrentDisplayLanguage('en-US');
                setAvailableSlideLanguages(['en-US']);
            }

            if (slideData) {
                setAssessmentVisibility(prev => ({
                    ...prev,
                    [session.session_code]: {
                        show_pre_assessment: showPreAssessment,
                        show_post_assessment: showPostAssessment
                    }
                }));

                setSessionSlides(slideData);
                const segmentsList = buildSegmentsList(slideData, session);
                if (setSegments) setSegments(segmentsList);

                if (segmentsList.length > 0) {
                    if (startAtLastSegment) {
                        const lastSegment = segmentsList[segmentsList.length - 1];
                        if (landOnLastSlideRef) landOnLastSlideRef.current = true;
                        if (setActiveSegment) setActiveSegment(lastSegment.id);
                        setSelectedSection('Session Details');
                        if (setCurrentSegment) setCurrentSegment(segmentsList.length - 1);
                    } else {
                        if (setActiveSegment) setActiveSegment(segmentsList[0].id);
                        if (setCurrentSlide) setCurrentSlide(0);
                        setSelectedSection('Session Details');
                        if (setCurrentSegment) setCurrentSegment(0);
                    }
                }
            } else {
                setSessionSlides({});
                if (setSegments) setSegments([]);
            }
        } catch (err) {
            setSessionSlides({});
            if (setSegments) setSegments([]);
            console.error("Error fetching slides:", err);
        } finally {
            setSlidesLoading(false);
        }
    }, [isPublicPreview, programData?.code, code, fetchSlidePreview, selectedPreviewLanguage, showEnglishContent, buildSegmentsList, setSegments, landOnLastSlideRef, setActiveSegment, setCurrentSegment, setCurrentSlide]);

    // Fetch Modules
    useEffect(() => {
        const fetchModules = async () => {
            if (!code) {
                setModulesData([]);
                setLoading(false);
                setInitialLoading(false);
                return;
            }

            try {
                setLoading(true);
                const res = await (isPublicPreview ? getUnrestrictedLearningProgramPreviewService(code) : fetchProgramPreview(code));
                if (!res?.data?.is_outline_ready) {
                    toastWarning(tUi('preview.toast.outlineNotReady', uiLocale));
                    setModulesData([]);
                    setLoading(false);
                    setInitialLoading(false);
                    return;
                }

                setPreviewResponse(res?.data);

                const previews = res?.data?.program_layout_previews;
                const availableLangs = previews && typeof previews === 'object' ? Object.keys(previews) : [];
                const initialPreferredLang = availableLangs.length > 1
                    ? (availableLangs.find(l => l !== 'en-US') || 'en-US')
                    : (availableLangs[0] || 'en-US');

                const {
                    program,
                    sessionAssessments: sessAssess,
                    language,
                    languages
                } = getProgramPreviewAndAssessmentsFromResponse(res?.data, initialPreferredLang);

                setAvailablePreviewLanguages(languages);
                setSelectedPreviewLanguage(language);
                setCurrentDisplayLanguage(language);

                if (program) {
                    if (!program.modules || !Array.isArray(program.modules)) {
                        setModulesData([]);
                        setLoading(false);
                        setInitialLoading(false);
                        return;
                    }

                    const mappedModules = mapProgramToSidebar(program);
                    setModulesData(mappedModules);
                    setSessionAssessments(sessAssess);

                    if (mappedModules.length > 0) {
                        let firstAvailableSession = null;
                        let moduleIdx = -1;
                        let unitIdx = -1;

                        outerLoop: for (let mIdx = 0; mIdx < mappedModules.length; mIdx++) {
                            const module = mappedModules[mIdx];
                            if (!module.units || module.units.length === 0) continue;

                            for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                                const unit = module.units[uIdx];
                                if (!unit.lessons || unit.lessons.length === 0) continue;

                                for (const session of unit.lessons) {
                                    firstAvailableSession = session;
                                    moduleIdx = mIdx;
                                    unitIdx = uIdx;
                                    break outerLoop;
                                }
                            }
                        }

                        if (firstAvailableSession && moduleIdx >= 0 && unitIdx >= 0) {
                            setOpenModules(prev => ({ ...prev, [moduleIdx]: true }));
                            setOpenUnits(prev => ({ ...prev, [`${moduleIdx}-${unitIdx}`]: true }));

                            setTimeout(() => {
                                if (fetchFirstSessionContentRef.current) {
                                    fetchFirstSessionContentRef.current(firstAvailableSession, sessAssess);
                                }
                            }, 1000);
                        } else {
                            setInitialLoading(false);
                        }
                    } else {
                        setInitialLoading(false);
                    }
                }
            } catch (err) {
                console.error('Error fetching modules:', err);
                setModulesData([]);
                setInitialLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, isPublicPreview, readOnly]);

    // Synchronize sidebar and assessments when display language changes
    useEffect(() => {
        if (!previewResponse) return;

        const { program, sessionAssessments: sessAssess } = getProgramPreviewAndAssessmentsFromResponse(previewResponse, currentDisplayLanguage);

        if (program) {
            const mappedModules = mapProgramToSidebar(program);
            setModulesData(mappedModules);
            setSessionAssessments(sessAssess);

            if (selectedSession) {
                let localizedSession = null;
                for (const mod of mappedModules) {
                    for (const unit of mod.units || []) {
                        const found = unit.lessons?.find(l => l.session_code === selectedSession.session_code);
                        if (found) {
                            localizedSession = found;
                            break;
                        }
                    }
                    if (localizedSession) break;
                }

                if (localizedSession) {
                    const updatedSession = {
                        ...selectedSession,
                        lesson_title: localizedSession.lesson_title,
                        session_learning_objectives: localizedSession.session_learning_objectives
                    };
                    setSelectedSession(updatedSession);

                    if (sessionSlides && Object.keys(sessionSlides).length > 0 && setSegments) {
                        setSegments(buildSegmentsList(sessionSlides, updatedSession));
                    }
                }
            }
        }
    }, [previewResponse, currentDisplayLanguage, selectedSession?.session_code, sessionSlides, buildSegmentsList, setSegments]);

    // Polling content status
    const fetchContentStatus = useCallback(async () => {
        if (isPublicPreview) return;
        try {
            const res = await getLearningProgramStatusService(code);
            lastStatusFetchTimeRef.current = Date.now();

            if (res?.data?.contentStatus) {
                const newStatuses = res.data.contentStatus;

                setContentStatus(prevStatuses => {
                    const newlyCompleted = [];

                    Object.keys(newStatuses).forEach(sessionCode => {
                        const prevStatusData = prevStatuses[sessionCode];
                        const newStatusData = newStatuses[sessionCode];

                        const prevStatus = typeof prevStatusData === 'object' ? prevStatusData.status : prevStatusData;
                        const newStatus = typeof newStatusData === 'object' ? newStatusData.status : newStatusData;

                        if (prevStatus === 'in_progress' && newStatus === 'done') {
                            newlyCompleted.push(sessionCode);
                        }
                    });

                    if (!selectedSession && newlyCompleted.length > 0 && modulesData.length > 0) {
                        let foundSession = false;
                        for (const module of modulesData) {
                            if (!module.units || foundSession) continue;
                            for (const unit of module.units) {
                                if (!unit.lessons || foundSession) continue;
                                for (const session of unit.lessons) {
                                    if (newlyCompleted.includes(session.session_code)) {
                                        if (fetchFirstSessionContentRef.current) {
                                            fetchFirstSessionContentRef.current(session);
                                        }
                                        foundSession = true;
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    return newStatuses;
                });

                const anyInProgress = Object.values(newStatuses).some(statusData => {
                    const status = typeof statusData === 'object' ? statusData.status : statusData;
                    return status === 'in_progress';
                });
                setAllSessionsDone(!anyInProgress);

                if (!anyInProgress) {
                    setStatusPolling(false);
                }
            }
        } catch (err) {
            console.error('Error fetching content status:', err);
            setContentStatus({});
        }
    }, [code, isPublicPreview, selectedSession, modulesData]);

    // Initial content status fetch and start polling
    useEffect(() => {
        if (!code || isPublicPreview) return;

        const timer = setTimeout(() => {
            fetchContentStatus();
            setStatusPolling(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, [code, isPublicPreview, fetchContentStatus]);

    // Status polling interval (5s)
    useInterval(() => {
        if (isPublicPreview) return;
        if (statusPolling) {
            const now = Date.now();
            const timeSinceLastFetch = lastStatusFetchTimeRef.current ? now - lastStatusFetchTimeRef.current : null;

            if (timeSinceLastFetch === null || timeSinceLastFetch >= 5000) {
                fetchContentStatus();
            }
        }
    }, statusPolling ? 5000 : null);

    // Lazy load first ready session once contentStatus is available
    useEffect(() => {
        if (Object.keys(contentStatus).length === 0 || selectedSession) {
            return;
        }

        if (modulesData.length > 0) {
            let firstReadySession = null;
            let moduleIdx = -1;
            let unitIdx = -1;

            outerLoop: for (let mIdx = 0; mIdx < modulesData.length; mIdx++) {
                const module = modulesData[mIdx];
                if (!module.units || module.units.length === 0) continue;

                for (let uIdx = 0; uIdx < module.units.length; uIdx++) {
                    const unit = module.units[uIdx];
                    if (!unit.lessons || unit.lessons.length === 0) continue;

                    for (const session of unit.lessons) {
                        const sessionStatusData = contentStatus[session.session_code];
                        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;
                        if (sessionStatus === 'done') {
                            firstReadySession = session;
                            moduleIdx = mIdx;
                            unitIdx = uIdx;
                            break outerLoop;
                        }
                    }
                }
            }

            if (firstReadySession && moduleIdx >= 0 && unitIdx >= 0) {
                setOpenModules(prev => ({ ...prev, [moduleIdx]: true }));
                setOpenUnits(prev => ({ ...prev, [`${moduleIdx}-${unitIdx}`]: true }));
                if (fetchFirstSessionContentRef.current) {
                    fetchFirstSessionContentRef.current(firstReadySession);
                }
            } else {
                setInitialLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentStatus, modulesData, selectedSession]);

    // Sidebar accordion toggles
    const handleToggleModuleDropdown = useCallback((moduleIndex) => {
        setOpenModules(prev => ({
            ...prev,
            [moduleIndex]: !prev[moduleIndex]
        }));
    }, []);

    const handleToggleUnitDropdown = useCallback((moduleIndex, unitIndex) => {
        setOpenUnits(prev => ({
            ...prev,
            [`${moduleIndex}-${unitIndex}`]: !prev[`${moduleIndex}-${unitIndex}`]
        }));
    }, []);

    const handleToggleSessionDropdown = useCallback((session) => {
        const sessionCode = session.session_code;
        const sessionStatusData = contentStatus[sessionCode];
        const sessionStatus = typeof sessionStatusData === 'object' ? sessionStatusData.status : sessionStatusData;

        if (sessionStatus === 'failed') {
            toastWarning(tUi('preview.toast.sessionNotAvailable', uiLocale, { title: session.lesson_title }));
            return;
        }

        if (sessionStatus === 'in_progress') {
            toastWarning(tUi('preview.toast.sessionGenerating', uiLocale, { title: session.lesson_title }));
            return;
        }

        if (!openSessions[sessionCode]) {
            const newOpenSessions = {};
            Object.keys(openSessions).forEach(key => { newOpenSessions[key] = false; });
            newOpenSessions[sessionCode] = true;
            setOpenSessions(newOpenSessions);

            if (selectedSession?.session_code !== sessionCode) {
                if (setSegments) setSegments([]);
                if (setActiveSegment) setActiveSegment(null);
                setCachedSlideData({});
                setAvailableSlideLanguages([]);
            }

            fetchSessionSlides(session);
        } else {
            setOpenSessions(prev => ({
                ...prev,
                [sessionCode]: !prev[sessionCode]
            }));
        }
    }, [contentStatus, openSessions, selectedSession?.session_code, uiLocale, setSegments, setActiveSegment, fetchSessionSlides]);

    // DQ Competency code metadata fetch
    const fetchV3Level7DQCodeList = useCallback(async () => {
        if (isPublicPreview) return [];
        try {
            const res = await listV3Level7DQCodeService(uiLocale);
            const list = res?.data?.metaData || [];
            setDqcoList(list);
            return list;
        } catch (err) {
            setDqcoList([]);
            return [];
        }
    }, [isPublicPreview, uiLocale]);

    useEffect(() => {
        fetchV3Level7DQCodeList();
    }, [fetchV3Level7DQCodeList]);

    const handleModuleCodeClick = useCallback(async (module, dqco) => {
        let list = dqcoList;
        if (list.length === 0) {
            setDqcoLoadingCode(dqco);
            try {
                list = await fetchV3Level7DQCodeList();
            } finally {
                setDqcoLoadingCode(null);
            }
        }

        const dqcoObj = list.find(item => item.dqco_no === dqco);

        setPopupContent({
            code: dqco,
            dqc_no: dqcoObj?.dqc_no,
            title: dqcoObj?.full_name,
            description: dqcoObj?.definition
        });
    }, [dqcoList, fetchV3Level7DQCodeList]);

    // Helper to get assessment items for the active session/segment
    const getActiveAssessmentItems = useCallback((activeSeg) => {
        const sessKey = selectedSession?.session_code;
        const assess = sessionAssessments?.[sessKey] || {};
        if (activeSeg === '__PRE_ASSESSMENT__') {
            return assess.pre || [];
        }
        if (activeSeg === '__POST_ASSESSMENT__') {
            return assess.post || [];
        }
        return [];
    }, [selectedSession, sessionAssessments]);

    // Direct language switcher handler
    const handleSelectLanguage = useCallback((targetLang) => {
        if (!targetLang || !cachedSlideData[targetLang]) return;

        const languageData = cachedSlideData[targetLang];

        setSelectedPreviewLanguage(targetLang);
        setCurrentDisplayLanguage(targetLang);

        if (languageData.slide_preview) {
            setSessionSlides(languageData.slide_preview);
            if (setSegments && selectedSession) {
                setSegments(buildSegmentsList(languageData.slide_preview, selectedSession));
            }
        }

        setIntroVideoUrl(languageData.IntroVideo ?? null);
        setIntroVideoVtt(languageData.IntroVideoVtt ?? null);

        if (selectedSession) {
            const { show_pre_assessment = true, show_post_assessment = true } = languageData;
            setAssessmentVisibility(prev => ({
                ...prev,
                [selectedSession.session_code]: { show_pre_assessment, show_post_assessment }
            }));
        }
    }, [cachedSlideData, selectedSession, buildSegmentsList, setSegments]);

    return {
        programData,
        setProgramData,
        modulesData,
        previewResponse,
        selectedSession,
        setSelectedSession,
        selectedSection,
        setSelectedSection,
        openModules,
        setOpenModules,
        openUnits,
        setOpenUnits,
        openSessions,
        setOpenSessions,
        loading,
        initialLoading,
        slidesLoading,
        contentStatus,
        allSessionsDone,
        statusPolling,
        availablePreviewLanguages,
        selectedPreviewLanguage,
        setSelectedPreviewLanguage,
        currentDisplayLanguage,
        setCurrentDisplayLanguage,
        availableSlideLanguages,
        setAvailableSlideLanguages,
        cachedSlideData,
        setCachedSlideData,
        sessionSlides,
        setSessionSlides,
        sessionAssessments,
        setSessionAssessments,
        introVideoUrl,
        setIntroVideoUrl,
        introVideoVtt,
        setIntroVideoVtt,
        assessmentVisibility,
        setAssessmentVisibility,
        dqcoList,
        dqcoLoadingCode,
        popupContent,
        setPopupContent,
        sidebarProgramName,
        uiLocale,
        ttsLang,
        buildSegmentsList,
        fetchFirstSessionContent,
        fetchSessionSlides,
        handleSelectLanguage,
        handleToggleModuleDropdown,
        handleToggleUnitDropdown,
        handleToggleSessionDropdown,
        handleModuleCodeClick,
        getActiveAssessmentItems,
        assessmentModalOpen,
        assessmentModalAction,
        handleOpenAssessmentModal,
        handleCloseAssessmentModal,
        handleConfirmAssessmentVisibility,
        assessmentModal: {
            open: assessmentModalOpen,
            action: assessmentModalAction,
            onClose: handleCloseAssessmentModal,
            onConfirm: handleConfirmAssessmentVisibility,
        },
    };
};

export const useProgramPreviewData = useProgramData;
export default useProgramData;
